// The four suits as the game's own icons (owner 2026-10-26: the text glyphs ♠ ♣ ♥ ♦ came from the device's font and
// looked different on every system): simple pip shapes like the cards', red hearts and diamonds, spades and clubs in
// the text colour (white on the dark plates, ink in the score window). `withSuits` puts them in a translated text.
import type { ReactNode } from 'react';
import type { HSuit } from './rules';

const SHAPES: Record<HSuit, ReactNode> = {
  H: <path d="M12 21.4C11.5 21 2.2 14.8 2.2 8.5 2.2 5.2 4.7 2.8 7.7 2.8c1.9 0 3.4 1 4.3 2.4.9-1.4 2.4-2.4 4.3-2.4 3 0 5.5 2.4 5.5 5.7 0 6.3-9.3 12.5-9.8 12.9z" />,
  D: <path d="M12 1.8 20.2 12 12 22.2 3.8 12z" />,
  S: <path d="M12 1.6c-.5.7-9.4 7.1-9.4 12.5 0 2.9 2.2 5 4.9 5 1.7 0 3.1-.8 3.8-1.9-.2 1.9-.9 3.3-2.4 4.6h6.2c-1.5-1.3-2.2-2.7-2.4-4.6.7 1.1 2.1 1.9 3.8 1.9 2.7 0 4.9-2.1 4.9-5 0-5.4-8.9-11.8-9.4-12.5z" />,
  C: (
    <>
      <circle cx="12" cy="6.6" r="4.4" />
      <circle cx="6.4" cy="13.4" r="4.4" />
      <circle cx="17.6" cy="13.4" r="4.4" />
      <path d="M11 11h2l1.4 11.2H9.6z" />
    </>
  ),
};
const NAMES: Record<HSuit, string> = { H: '♥', D: '♦', S: '♠', C: '♣' };
const BY_CHAR: Record<string, HSuit> = { '♥': 'H', '♦': 'D', '♠': 'S', '♣': 'C' };

export function SuitIcon({ s }: { s: HSuit }) {
  return (
    <svg className="ht-suit" data-suit={s} viewBox="0 0 24 24" role="img" aria-label={NAMES[s]}>
      {SHAPES[s]}
    </svg>
  );
}

/** A text with its ♠ ♣ ♥ ♦ (also ♡ ♢ ♤ ♧ and an emoji variation selector) drawn as the suit icons. */
export function withSuits(text: string): ReactNode[] {
  return text
    .replace(/[︎️]/g, '')
    .replace(/♡/g, '♥')
    .replace(/♢/g, '♦')
    .replace(/♤/g, '♠')
    .replace(/♧/g, '♣')
    .split(/([♠♣♥♦])/)
    .map((part, i) => (BY_CHAR[part] ? <SuitIcon key={i} s={BY_CHAR[part]!} /> : part));
}

/** The crown on the leader's score tile (owner 2026-10-26: an icon in the game's style, not the ♛ of the device's
 *  font): flat, three points with balls, a band. */
export function CrownIcon() {
  return (
    <svg className="hs-crown" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M3 8.2l4.6 3.9L12 5.6l4.4 6.5L21 8.2l-1.9 9.3H4.9z" />
      <rect fill="currentColor" x="4.9" y="18.6" width="14.2" height="2.6" rx="1" />
      <circle fill="currentColor" cx="3" cy="7.4" r="1.7" />
      <circle fill="currentColor" cx="12" cy="4.4" r="1.7" />
      <circle fill="currentColor" cx="21" cy="7.4" r="1.7" />
    </svg>
  );
}

/** Who led the trick, beside the name over its place on a phone: a small pointer. */
export function LeadIcon() {
  return (
    <svg className="ht-leadmark" viewBox="0 0 9 11" aria-hidden="true">
      <path fill="currentColor" d="M1 1.2c0-.8.9-1.2 1.5-.7l5.8 4.3c.5.4.5 1.1 0 1.4L2.5 10.5c-.6.5-1.5.1-1.5-.7z" />
    </svg>
  );
}

